import {
  type ColumnBlockModel,
  type ColumnRowBlockModel,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import { BlockComponent } from '@blocksuite/std';
import { css, html, nothing } from 'lit';

export class ColumnRowBlockComponent extends BlockComponent<ColumnRowBlockModel> {
  static override styles = css`
    affine-column-row {
      display: block;
      margin: 8px 0;
      min-width: 0;
    }
    .affine-column-row-children {
      display: flex;
      align-items: stretch;
      gap: 24px;
    }
    @media (max-width: 600px) {
      .affine-column-row-children {
        flex-direction: column;
        gap: 8px;
      }
    }
  `;

  override renderBlock() {
    return html`<div class="affine-column-row-children">
      ${this.renderChildren(this.model)}
    </div>`;
  }
}

export class ColumnBlockComponent extends BlockComponent<ColumnBlockModel> {
  static override styles = css`
    affine-column {
      display: block;
      position: relative;
      flex: 1 1 0;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .affine-column-children {
      min-height: 32px;
    }
    .affine-column-resizer {
      position: absolute;
      right: -6px;
      top: 0;
      bottom: 0;
      width: 6px;
      padding: 0;
      border: 0;
      background: transparent;
      cursor: col-resize;
      touch-action: none;
    }
    .affine-column-resizer:hover,
    .affine-column-resizer:focus-visible {
      background: var(--affine-primary-color);
      border-radius: 4px;
    }
    .affine-column-empty {
      width: 100%;
      min-height: 32px;
      border: 1px dashed var(--affine-border-color);
      background: transparent;
      color: var(--affine-text-secondary-color);
      cursor: text;
    }
    @media (max-width: 600px) {
      .affine-column-resizer {
        display: none;
      }
    }
  `;

  private _cancelResize: (() => void) | null = null;

  private _addParagraph() {
    if (this.store.readonly) return;
    const id = this.store.addBlock('affine:paragraph', {}, this.model);
    focusTextModel(this.std, id);
  }

  private _resize(event: PointerEvent) {
    if (this.store.readonly || event.button !== 0) return;
    const next = this.store.getNext(this.model) as ColumnBlockModel | null;
    const nextView = next && this.std.view.getBlock(next.id);
    if (!next || !nextView) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const leftWidth = this.getBoundingClientRect().width;
    const rightWidth = nextView.getBoundingClientRect().width;
    const totalPixels = leftWidth + rightWidth;
    const totalWeight = this.model.props.width + next.props.width;
    let ratio = leftWidth / totalPixels;
    const move = (e: PointerEvent) => {
      if (e.pointerId !== event.pointerId) return;
      const min = Math.min(80 / totalPixels, 0.4);
      ratio = Math.max(
        min,
        Math.min(1 - min, (leftWidth + e.clientX - startX) / totalPixels)
      );
      this.style.flexGrow = String(totalWeight * ratio);
      nextView.style.flexGrow = String(totalWeight * (1 - ratio));
    };
    const clean = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      this.style.flexGrow = String(this.model.props.width);
      nextView.style.flexGrow = String(next.props.width);
      this._cancelResize = null;
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== event.pointerId) return;
      if (this.store.getBlock(next.id) && !this.store.readonly) {
        this.store.captureSync();
        this.store.transact(() => {
          this.store.updateBlock(this.model, { width: totalWeight * ratio });
          this.store.updateBlock(next, { width: totalWeight * (1 - ratio) });
        });
        this.store.captureSync();
      }
      clean();
    };
    const cancel = (e: PointerEvent) => {
      if (e.pointerId === event.pointerId) clean();
    };
    this._cancelResize?.();
    this._cancelResize = clean;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  }

  override disconnectedCallback() {
    this._cancelResize?.();
    super.disconnectedCallback();
  }

  override renderBlock() {
    const width = this.model.props.width;
    this.style.flexGrow = String(
      Number.isFinite(width) && width > 0 ? width : 1
    );
    return html`
      <div class="affine-column-children">
        ${this.renderChildren(this.model)}
        ${
          !this.model.children.length && !this.store.readonly
            ? html`<button
                class="affine-column-empty"
                @click=${this._addParagraph}
              >
                +
              </button>`
            : nothing
        }
      </div>
      ${
        this.store.getNext(this.model) && !this.store.readonly
          ? html`<button
              class="affine-column-resizer"
              contenteditable="false"
              aria-label="Resize columns"
              @pointerdown=${this._resize}
            ></button>`
          : nothing
      }
    `;
  }
}
